/**
 * B632 (c) — the guest's own pages: sign in with a link (649:2) and the
 * orders bought with that email (649:111). Faces only; `meApp.tsx` holds the
 * calls. Built to the frames' ids; the sidecars are pulled beside them
 * (`spec/redline-guest-*.figma.json`).
 */
import type { ReactElement, ReactNode } from "react";
import { Banner, Button, DoorRow, EmptyState, Input, Skeleton, StageChip } from "@transact/ui";
import { clockWords, type MeCard, type MeOrderFace } from "../lib/me.js";

export { MeChangeSheet } from "./meChangeSheet.js";

/** 649:2 / 649:111 — the brand tile and name on the left, MY TICKETS (this page) on the right. */
function MeBar(props: { readonly businessName?: string | undefined; readonly logoUrl?: string | undefined; readonly here?: string | undefined }): ReactElement {
  return (
    <header className="me-bar" data-redline="me-bar">
      <p className="me-brand">
        {/* B722 — the venue's logo from the guest door rides the tile; decorative, the name beside it is the text */}
        <span className="me-brand-tile" aria-hidden="true">
          {props.logoUrl !== undefined ? <img src={props.logoUrl} alt="" /> : null}
        </span>
        {props.businessName ?? "Your tickets"}
      </p>
      <p className="me-here" aria-current="page" data-redline="me-here">
        {props.here ?? "My tickets"}
      </p>
    </header>
  );
}

/** B709 leg 2 — shared with the member portal (643:2), whose bar names MY MEMBERSHIP. */
/**
 * B1009 (WCAG 3.2.3) — the signed-in buyer's account row: who is signed in,
 * the other /me page's door, and Sign out. The shell draws it, first under
 * the bar, so Sign out sits in the same place on every signed-in face; no
 * page draws one of its own.
 */
export interface MeAccount {
  readonly email?: string | undefined;
  /** How the row says the email ("Signed in as …" on the portal); default the bare address. */
  readonly emailWords?: string | undefined;
  readonly door?: { readonly href: string; readonly label: string } | undefined;
  readonly onSignOut: () => void;
}

export function MeShell(props: {
  readonly businessName?: string | undefined;
  readonly logoUrl?: string | undefined;
  readonly label: string;
  readonly wide?: boolean;
  readonly here?: string;
  /** B1009 — present on every signed-in face; absent on sign-in. */
  readonly account?: MeAccount | undefined;
  readonly children: ReactNode;
}): ReactElement {
  const account = props.account;
  return (
    <main className={props.wide === true ? "me me--wide" : "me"} aria-label={props.label}>
      <MeBar businessName={props.businessName} logoUrl={props.logoUrl} here={props.here} />
      <div className="me-body">
        {account !== undefined ? (
          <div className="me-who" data-redline="me-who">
            <p className="me-email" data-redline="me-email">
              {account.emailWords ?? account.email ?? ""}
            </p>
            {account.door !== undefined ? (
              /* dead-ok: the href navigates — Button draws a real <a> for it, the press needs no handler */
              <Button variant="textlink-accent" href={account.door.href} onClick={() => undefined}>
                {account.door.label}
              </Button>
            ) : null}
            <Button variant="textlink-accent" onClick={account.onSignOut}>
              Sign out
            </Button>
          </div>
        ) : null}
        {props.children}
      </div>
    </main>
  );
}

export type MeSignInPhase =
  | { readonly kind: "ask"; readonly refused?: boolean; readonly error?: string | undefined; readonly busy?: boolean; readonly failed?: boolean }
  | { readonly kind: "sent"; readonly to: string; readonly resendIn: number; readonly busy?: boolean };

/** 649:2 — no password: the email, one door, and the same answer whether or not the address has orders. */
export function MeSignIn(props: {
  readonly phase: MeSignInPhase;
  readonly email: string;
  readonly businessName?: string | undefined;
  readonly logoUrl?: string | undefined;
  readonly onEmail: (email: string) => void;
  readonly onSend: () => void;
  readonly onAnother: () => void;
  /** B722 — the buyer asked for their orders (`/me/orders[/<id>]`) signed out: one sentence says why the page asks. */
  readonly asked?: boolean;
  /** B709 leg 2 — what the buyer asked for: their orders (default) or their membership. */
  readonly askedFor?: "orders" | "membership";
}): ReactElement {
  const { phase } = props;
  return (
    <MeShell businessName={props.businessName} logoUrl={props.logoUrl} label="Sign in to your tickets">
      <h1 className="me-title" data-redline="me-title">Your tickets, no password</h1>
      {phase.kind === "ask" ? (
        <>
          {props.asked === true ? <p className="me-asked">{`Sign in to see your ${props.askedFor ?? "orders"} — we'll email you a link.`}</p> : null}
          <p className="me-lede" data-redline="me-lede">Enter the email you bought with. We'll send a link that signs you in — it works once and expires in 15 minutes.</p>
          {phase.refused === true ? <Banner tone="warn">That sign-in link has already been used or has expired — ask for a fresh one below.</Banner> : null}
          {phase.failed === true ? <Banner tone="warn">We couldn't send the link just now. Nothing was sent — try again in a moment.</Banner> : null}
          {/* R14 (the B683 shape) — a form, so Enter in the field clicks the submit door; the press is the one send */}
          <form className="me-form" aria-label="Email me a sign-in link" data-redline="me-form" onSubmit={(e) => e.preventDefault()}>
            <Input id="me-email" autoComplete="email" required label="Email" type="email" placeholder="you@example.com" value={props.email} onChange={props.onEmail} error={phase.error} />
            <Button variant="primary" type="submit" busy={phase.busy === true} onClick={props.onSend}>
              Email me a sign-in link
            </Button>
          </form>
          <p className="me-foot" data-redline="me-foot">No account to make. Your confirmation email has a link that does the same thing.</p>
        </>
      ) : (
        <section className="me-sent" aria-labelledby="me-sent-title" role="status">
          <h2 className="me-sent-title" id="me-sent-title">
            Check your email
          </h2>
          <p className="me-sent-body" id="me-sent-body">
            {`We sent a sign-in link to ${phase.to}. Open it on this phone to see your tickets. Didn't get it? Check spam, or ${phase.resendIn > 0 ? `resend in ${clockWords(phase.resendIn)}` : "resend it now"}.`}
          </p>
          <div className="me-sent-doors">
            <Button variant="neutral" disabled={phase.resendIn > 0} describedBy="me-sent-body" busy={phase.busy === true} onClick={props.onSend}>
              Resend
            </Button>
            <Button variant="ghost" onClick={props.onAnother}>
              Use another email
            </Button>
          </div>
        </section>
      )}
    </MeShell>
  );
}

function OrderCard(props: {
  readonly card: MeCard;
  readonly live: boolean;
  readonly busy: boolean;
  readonly error?: string | undefined;
  readonly onTickets: () => void;
  readonly onManage?: (() => void) | undefined;
}): ReactElement {
  const { card } = props;
  return (
    <li className={`me-order ${props.live ? "me-order--live" : "me-order--past"}`} data-order={card.orderId}>
      {props.live ? (
        <>
          <div className="me-order-head">
            <h3 className="me-order-show">{card.show}</h3>
            <StageChip tone={card.chip.tone}>{card.chip.word}</StageChip>
          </div>
          {card.when !== undefined ? <p className="me-order-when">{card.when}</p> : null}
          <p className="me-order-what">{[card.what, card.code].filter((s) => s !== undefined && s !== "").join(" · ")}</p>
          {props.error !== undefined ? <Banner tone="warn">{props.error}</Banner> : null}
          <div className="me-order-doors">
            <Button variant="primary" busy={props.busy} onClick={props.onTickets}>
              Tickets
            </Button>
            {/* B632 (c) region 2 — 649:111's second door: the order's own page (650:2) */}
            {props.onManage !== undefined ? (
              <Button variant="neutral" onClick={props.onManage} ariaLabel={`Manage ${card.show}`}>
                Manage
              </Button>
            ) : null}
          </div>
        </>
      ) : (
        /* 649:111 — a past order is ONE row: the words column beside the chip */
        <>
          <div className="me-order-words">
            <h3 className="me-order-show">{card.show}</h3>
            <p className="me-order-what">{[card.when, card.code].filter((s) => s !== undefined).join(" · ")}</p>
            <p className="me-order-what">{card.what}</p>
          </div>
          <StageChip tone={card.chip.tone}>{card.chip.word}</StageChip>
        </>
      )}
    </li>
  );
}

/** 649:111 — the orders bought with this email, what is coming up first. */
export function MeOrders(props: {
  readonly email: string;
  readonly businessName?: string | undefined;
  readonly logoUrl?: string | undefined;
  /** undefined = still reading. */
  readonly cards: { readonly upcoming: readonly MeCard[]; readonly past: readonly MeCard[] } | undefined;
  readonly error?: string | undefined;
  readonly ticketsBusy?: string | undefined;
  readonly ticketsError?: { readonly orderId: string; readonly message: string } | undefined;
  readonly onTickets: (orderId: string) => void;
  /** B632 (c) region 2 — MANAGE → the order's page. */
  readonly onManage?: ((orderId: string) => void) | undefined;
  readonly onSignOut: () => void;
  readonly onRetry?: (() => void) | undefined;
  /** B815 — the signed-in guest holds a membership: its door and one line naming it. */
  readonly membership?: { readonly href: string; readonly words: string } | undefined;
}): ReactElement {
  const { cards } = props;
  const onManage = props.onManage;
  return (
    <MeShell
      businessName={props.businessName}
      logoUrl={props.logoUrl}
      label="Your orders"
      account={{
        email: props.email,
        ...(props.membership !== undefined ? { door: { href: props.membership.href, label: "My membership ›" } } : {}),
        onSignOut: props.onSignOut,
      }}
    >
      <h1 className="me-title" data-redline="me-title">Your orders</h1>
      {props.membership !== undefined ? (
        /* B815 — a member sees their membership named here, with its door; the portal keeps the cards and the renew. */
        <p className="me-membership-line" data-redline="me-membership-line">
          {props.membership.words}{" "}
          <a href={props.membership.href}>See your membership ›</a>
        </p>
      ) : null}
      {/* B316 — the refusal stands above whatever the page already holds; it never replaces it */}
      {props.error !== undefined ? (
        <Banner tone="warn">
          {props.error}
          {props.onRetry !== undefined ? (
            <Button variant="textlink" onClick={props.onRetry}>
              Try again
            </Button>
          ) : null}
        </Banner>
      ) : null}
      {cards === undefined ? (
        props.error !== undefined ? null : (
        <div role="status" aria-label="Reading your orders">
          <Skeleton lines={6} />
        </div>
        )
      ) : cards.upcoming.length === 0 && cards.past.length === 0 ? (
        <EmptyState
          title={`No orders under ${props.email} yet`}
          body="Tickets bought with this email show here. Bought with another address? Sign out and use that one."
        />
      ) : (
        <>
          {cards.upcoming.length > 0 ? (
            <section className="me-section" aria-labelledby="me-upcoming" data-redline="me-upcoming">
              <h2 className="me-section-label" id="me-upcoming">
                Coming up
              </h2>
              <ul className="me-orders">
                {cards.upcoming.map((c) => (
                  <OrderCard
                    key={c.orderId}
                    card={c}
                    live
                    busy={props.ticketsBusy === c.orderId}
                    error={props.ticketsError?.orderId === c.orderId ? props.ticketsError.message : undefined}
                    onTickets={() => props.onTickets(c.orderId)}
                    onManage={onManage !== undefined ? () => onManage(c.orderId) : undefined}
                  />
                ))}
              </ul>
            </section>
          ) : null}
          {cards.past.length > 0 ? (
            <section className="me-section" aria-labelledby="me-past" data-redline="me-past">
              <h2 className="me-section-label" id="me-past">
                Past
              </h2>
              <ul className="me-orders">
                {cards.past.map((c) => (
                  <OrderCard key={c.orderId} card={c} live={false} busy={false} onTickets={() => undefined} />
                ))}
              </ul>
            </section>
          ) : null}
        </>
      )}
      <p className="me-foot" data-redline="me-foot">Only orders bought with this email. Bought with another address? Sign in with that one — or ask the box office to merge them.</p>
    </MeShell>
  );
}

/* ---------------------------------------------------------------- 650:2 */

export type MeResendState = { readonly kind: "busy" } | { readonly kind: "sent" } | { readonly kind: "failed" };

/**
 * 650:2 @390 / 651:2 @1440 — one order, everything the buyer can use: the
 * tickets with their QR codes, what they can still do (the tenant's rule),
 * what they agreed to, and what they paid. One markup; the frame's two
 * columns at desktop widths are CSS (`.me-order-page` grid areas).
 */
export function MeOrderPage(props: {
  /** undefined = still reading (or the read failed — see `error`, or names nothing — see `missing`). */
  readonly face: MeOrderFace | undefined;
  readonly error?: string | undefined;
  /**
   * B1014 — the address names no order under this session (the fence's empty
   * answer: a mistyped or stale link, or an order bought with another email).
   * The signed-in email; the page draws the empty face with the way back.
   */
  readonly missing?: string | undefined;
  /** token → its QR image, drawn as each loads. */
  readonly qr: Readonly<Record<string, string>>;
  readonly backHref: string;
  readonly businessName?: string | undefined;
  readonly logoUrl?: string | undefined;
  readonly resend?: MeResendState | undefined;
  readonly onPrint: () => void;
  readonly onResend: () => void;
  readonly onSignOut: () => void;
  /** B1009 — the signed-in email, said in the shell's account row. */
  readonly email?: string | undefined;
  readonly onRetry?: (() => void) | undefined;
  /** B632 (e) — "Change the …" opens the Change sheet; absent = the row says the rule and opens nothing. */
  readonly onChange?: (() => void) | undefined;
  /** B632 (e2) — "Add tickets to this order" opens the Add sheet; absent = the row says the rule and opens nothing. */
  readonly onAdd?: (() => void) | undefined;
  /** B632 (e) — the night the order just moved to ("Sat, Oct 10 · 7:00 PM"). */
  readonly moved?: string | undefined;
}): ReactElement {
  const { face } = props;
  const account: MeAccount = { email: props.email, onSignOut: props.onSignOut };
  const back = (
    <p className="me-back" data-redline="me-back">
      {/* dead-ok: the href navigates — Button draws a real <a> for it, the press needs no handler */}
      <Button variant="textlink-accent" href={props.backHref} onClick={() => undefined}>
        ‹ Your orders
      </Button>
    </p>
  );
  /* B1014 — B1012's class on the buyer side: an address naming nothing is not a
     refusal, so there is nothing to retry and no reference — the empty face
     (the B134 / B708 / B1012 shape) and the way back, no id in the words. */
  if (face === undefined && props.missing !== undefined)
    return (
      <MeShell businessName={props.businessName} logoUrl={props.logoUrl} label="Your order" wide account={account}>
        <EmptyState
          title="No order at this address"
          body={`This order isn't under ${props.missing} — the link may be mistyped, or the order was bought with another email. Your orders lists every one that is.`}
          action={
            /* dead-ok: the href navigates — Button draws a real <a> for it, the press needs no handler */
            <Button variant="neutral" href={props.backHref} onClick={() => undefined}>
              Your orders
            </Button>
          }
        />
      </MeShell>
    );
  if (face === undefined)
    return (
      <MeShell businessName={props.businessName} logoUrl={props.logoUrl} label="Your order" wide account={account}>
        {back}
        {/* B316 — the refusal stands on its own line; the loading arm defers to it */}
        {props.error !== undefined ? (
          <Banner tone="warn">
            {props.error}
            {props.onRetry !== undefined ? (
              <Button variant="textlink" onClick={props.onRetry}>
                Try again
              </Button>
            ) : null}
          </Banner>
        ) : null}
        {props.error === undefined ? (
          <div role="status" aria-label="Reading your order">
            <Skeleton lines={8} />
          </div>
        ) : null}
      </MeShell>
    );
  const count = face.tickets.length;
  return (
    <MeShell businessName={props.businessName} logoUrl={props.logoUrl} label={`Your order — ${face.show}`} wide account={account}>
      <div className="me-order-page">
        <div className="me-op-head" data-redline="me-op-head">
          {back}
          <div className="me-op-title-row" data-redline="me-op-title-row">
            <h1 className="me-title me-op-title" data-redline="me-title">
              {face.show}
            </h1>
            <StageChip tone={face.chip.tone}>{face.chip.word}</StageChip>
          </div>
          {face.when !== undefined ? (
            <p className="me-op-when" data-redline="me-op-when">
              {face.when}
            </p>
          ) : null}
          <p className="me-op-facts" data-redline="me-op-facts">
            {face.facts}
          </p>
          {/* a move or a resend answers under the facts — in the head, so no grid row waits empty for it */}
          {props.moved !== undefined ? (
            <div className="me-op-note">
              <Banner tone="success">{`Moved to ${props.moved}. Your new tickets are below and on their way to your email — the old ones no longer scan.`}</Banner>
            </div>
          ) : null}
          {props.resend?.kind === "sent" ? (
            <div className="me-op-note">
              <Banner tone="success">We sent your tickets to your email again.</Banner>
            </div>
          ) : props.resend?.kind === "failed" ? (
            <div className="me-op-note">
              <Banner tone="warn">We couldn't send them just now. Nothing was sent — try again in a moment.</Banner>
            </div>
          ) : null}
        </div>
        {/* 651:2 — at desktop widths the doors stand on the head's right; at 390
            (650:2) the same presses live inside the tickets card (PRINT in its
            head, RESEND at its foot). One shows per width — the other is
            display:none, out of the tab order and the accessibility tree. */}
        <div className="me-op-doors" data-redline="me-op-doors">
          <Button variant="neutral" onClick={props.onPrint}>
            Print tickets
          </Button>
          {face.resend ? (
            <Button variant="neutral" busy={props.resend?.kind === "busy"} onClick={props.onResend}>
              Resend email
            </Button>
          ) : null}
        </div>
        <section className="me-panel me-op-tickets" aria-labelledby="me-op-tickets" data-redline="me-op-tickets">
          <div className="me-panel-head">
            <h2 className="me-section-label" id="me-op-tickets">{`Tickets · ${String(count)}`}</h2>
            <span className="me-op-print" data-redline="me-op-print">
              <Button variant="ghost" onClick={props.onPrint}>
                Print
              </Button>
            </span>
          </div>
          {count === 0 ? (
            <p className="me-op-quiet">No tickets to show on this order — the box office can tell you why.</p>
          ) : (
            <ul className="me-tickets">
              {face.tickets.map((t, i) => {
                const src = t.token !== undefined ? props.qr[t.token] : undefined;
                return (
                  <li className="me-ticket" key={t.entitlementId}>
                    {src !== undefined ? (
                      <img className="me-ticket-qr" src={src} alt={`QR code for ${t.name}, ticket ${String(i + 1)} of ${String(count)}`} width={72} height={72} />
                    ) : (
                      <span className={`me-ticket-qr ${t.token !== undefined ? "me-ticket-qr--wait" : "me-ticket-qr--none"}`} aria-hidden="true" />
                    )}
                    <div className="me-ticket-words">
                      <p className="me-ticket-name">{t.name}</p>
                      <p className="me-ticket-ref">{t.ref}</p>
                      {t.status !== undefined ? <StageChip tone={t.token !== undefined ? "confirmed" : "void"}>{t.status}</StageChip> : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {face.resend ? (
            <div className="me-op-resend" data-redline="me-op-resend">
              <Button variant="neutral" busy={props.resend?.kind === "busy"} onClick={props.onResend}>
                Resend email
              </Button>
            </div>
          ) : null}
        </section>
        {face.canDo !== undefined ? (
          <section className="me-panel me-op-cando" aria-labelledby="me-op-cando" data-redline="me-op-cando">
            <h2 className="me-section-label" id="me-op-cando">
              What you can still do
            </h2>
            <ul className="me-cando">
              {face.canDo.map((r) => (
                <li key={r.key} className={`me-cando-row${r.offered ? "" : " me-cando-row--off"}`} data-cando={r.key}>
                  <DoorRow
                    title={r.title}
                    body={r.body}
                    off={!r.offered}
                    {...(r.key === "change" && r.offered && props.onChange !== undefined ? { onPress: props.onChange } : {})}
                    {...(r.key === "add" && r.offered && props.onAdd !== undefined ? { onPress: props.onAdd } : {})}
                  />
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        <div className="me-op-side">
          {face.agreed !== undefined ? (
            <section className="me-panel" aria-labelledby="me-op-agreed" data-redline="me-op-agreed">
              <h2 className="me-section-label" id="me-op-agreed">
                You agreed to
              </h2>
              <ul className="me-agreed">
                {face.agreed.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </section>
          ) : null}
          <section className="me-panel" aria-labelledby="me-op-paid" data-redline="me-op-paid">
            <h2 className="me-section-label" id="me-op-paid">
              {face.paidOn !== undefined ? `Paid · ${face.paidOn}` : "Paid"}
            </h2>
            <dl className="me-paid">
              {face.paid.map((r) => (
                <div className="me-paid-row" key={r.label}>
                  <dt>{r.label}</dt>
                  <dd>{r.amount}</dd>
                </div>
              ))}
              <div className="me-paid-row me-paid-row--total">
                <dt>{face.total.label}</dt>
                <dd>{face.total.amount}</dd>
              </div>
              {/* B1021 leg 2 — how it was paid, when gift cards paid some of it */}
              {(face.paidWith ?? []).map((r) => (
                <div className="me-paid-row me-paid-row--tender" key={r.label}>
                  <dt>{r.label}</dt>
                  <dd>{r.amount}</dd>
                </div>
              ))}
            </dl>
          </section>
          {/* 651:2's help card; at 390 (650:2) the same words are the page's foot line */}
          <section className="me-panel me-op-help" aria-labelledby="me-op-help" data-redline="me-op-help">
            <h2 className="me-section-label" id="me-op-help">
              Need something else?
            </h2>
            <p className="me-op-quiet">Reply to your confirmation email — it reaches the box office. Bring your order code.</p>
          </section>
        </div>
        <p className="me-op-foot" data-redline="me-op-foot">
          Need something else? Reply to your confirmation email — it reaches the box office. Bring your order code.
        </p>
      </div>
    </MeShell>
  );
}
